import { Alert, Box, Button, CircularProgress, ListSubheader, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import * as React from 'react';

import { DeveloperApiError } from '../api';
import { ErrorAlert } from '../components';
import type { Integration, IntegrationAdd, IntegrationProbe, IntegrationSystem, SettingValue } from '../integrationTypes';
import { SLUG_PATTERN } from './AccountEditor';
import CredentialFields, { CredentialField, credentialsBody, credentialsComplete, fieldErrors, ProbeResult } from './CredentialFields';
import { useAction, useIntegrations } from './IntegrationsContext';
import { setupHelp } from './integrationLabels';
import SettingsEditor from './SettingsEditor';

/** Systems whose credentials go to a NAMED account (the spec's `IntegrationAdd.account`: "TourCMS only, and
 *  required there"; the backend's NAMED_ACCOUNT_CREDENTIALS). */
export const ACCOUNT_ON_ADD = new Set(['tourcms']);

/** The add flow's settings step: the system's `x-client: edit` keys, at their defaults or as chosen. */
const settingValues = (schema: Record<string, any>, chosen: Record<string, any>): Record<string, SettingValue> => (
    Object.fromEntries(Object.entries<Record<string, any>>(schema.properties || {})
        .filter(([, prop]) => prop && prop['x-client'] === 'edit')
        .map(([key, prop]) => [key, key in chosen
            ? { value: chosen[key], source: 'connection', mode: 'edit' }
            : { value: prop.default ?? null, source: 'default', mode: 'edit' }]))
);

const optionNote = (system: IntegrationSystem) => {
    if (system.connected) {
        return system.kind === 'fleet' ? 'Connected already' : 'Connected already: add an account to it instead';
    }
    if (!system.addable || system.connect === 'support') {
        return 'Curated Planet support connects it for you';
    }
    return null;
};

/** [+ Add booking system] (plan §1 Add connection), an inline new card: pick a system, its credentials with
 *  [Check] (where the list says it `can_check`), optionally its settings (`settings_schema`), then the new card
 *  shows its webhook addresses and token right away. The server assigns every ops key; the client never sends a
 *  source code. TourCMS also names the account its credentials go to. */
const AddConnectionFlow = ({ systems, onAdded, onClose }: {
    systems: IntegrationSystem[];
    onAdded: (connection: Integration) => void;
    onClose: () => void;
}) => {
    const { api, environment } = useIntegrations();
    const [code, setCode] = React.useState('');
    const [values, setValues] = React.useState<Record<string, string>>({});
    const [probe, setProbe] = React.useState<IntegrationProbe | null>(null);
    // An older list does not say which systems have a probe (`can_check`): a 409 not_supported answers it, once.
    const [noProbe, setNoProbe] = React.useState(false);
    const [account, setAccount] = React.useState('');
    const [settings, setSettings] = React.useState<Record<string, any>>({});
    const [editingSettings, setEditingSettings] = React.useState(false);
    const checking = useAction();
    const adding = useAction();
    const picked = systems.find((s) => s.system === code) || null;
    const offered = !!picked && picked.addable && picked.connect !== 'support' && !picked.connected;
    const fields: CredentialField[] = (picked?.credential_fields || []).map((f) => ({ ...f }));
    const withAccount = !!picked && ACCOUNT_ON_ADD.has(picked.system);
    const accountError = account && !SLUG_PATTERN.test(account)
        ? 'Lowercase letters, digits, - and _ only, starting with a letter or a digit (at most 64).'
        : null;
    const accountReady = !withAccount || (!!account && !accountError);
    const complete = offered && credentialsComplete(fields, values) && accountReady;
    const canCheck = picked?.can_check ?? !noProbe;
    const settingsSchema = picked?.settings_schema || null;
    const chosenSettings = settingsSchema ? settingValues(settingsSchema, settings) : {};
    const hasSettingsStep = Object.keys(chosenSettings).length > 0;
    const chosenCount = Object.keys(settings).length;
    const booking = systems.filter((s) => s.kind === 'booking');
    const fleet = systems.filter((s) => s.kind === 'fleet');

    const pick = (next: string) => {
        setCode(next);
        setValues({});
        setProbe(null);
        setNoProbe(false);
        setAccount('');
        setSettings({});
        setEditingSettings(false);
        checking.setError(null);
        adding.setError(null);
    };
    const check = async () => {
        if (!picked) {
            return;
        }
        setProbe(null);
        const outcome = await checking.run(() => api.check(picked.system, { credentials: credentialsBody(fields, values) }));
        if (outcome.ok) {
            setProbe(outcome.value);
        } else if (outcome.error instanceof DeveloperApiError && outcome.error.code === 'not_supported') {
            checking.setError(null);
            setNoProbe(true);
        }
    };
    const add = async () => {
        if (!picked || !complete) {
            return;
        }
        const body: IntegrationAdd = { system: picked.system, credentials: credentialsBody(fields, values) };
        if (withAccount) {
            body.account = account;
        }
        if (chosenCount > 0) {
            body.settings = settings;
        }
        const outcome = await adding.run(() => api.add(body));
        if (outcome.ok) {
            onAdded(outcome.value);
        }
    };
    const option = (system: IntegrationSystem) => {
        const note = optionNote(system);
        return (
            <MenuItem key={system.system} value={system.system} disabled={system.connected} sx={{ display: 'block' }}>
                {system.name}
                {note && <Typography variant="caption" color="text.secondary" component="div">{note}</Typography>}
            </MenuItem>
        );
    };
    const help = picked && picked.kind === 'booking' ? setupHelp(picked.setup_mode, picked.name, environment) : null;

    return (
        <Paper variant="outlined" component="section" aria-labelledby="add-connection-title" sx={{ p: { xs: 1.5, sm: 2 } }} data-testid="add-connection">
            {/* No <form>: vendor credentials must not read as a sign-up form to password managers. */}
            <Box>
                <Typography id="add-connection-title" variant="subtitle1" component="h2" sx={{ fontWeight: 500, mb: 1.5 }}>Add a booking system</Typography>
                <TextField
                    select
                    size="small"
                    label="System"
                    value={code}
                    onChange={(ev) => pick(ev.target.value)}
                    SelectProps={{ MenuProps: { sx: { zIndex: 10002 } } }}
                    inputProps={{ 'data-testid': 'system-picker' }}
                    sx={{ width: { xs: '100%', sm: 360 } }}
                >
                    {booking.length > 0 && <ListSubheader>Booking systems</ListSubheader>}
                    {booking.map(option)}
                    {fleet.length > 0 && <ListSubheader>Fleet tracking</ListSubheader>}
                    {fleet.map(option)}
                </TextField>

                {picked && !offered && !picked.connected && (
                    <Alert severity="info" sx={{ mt: 2 }} data-testid="contact-support">
                        Curated Planet support connects {picked.name} for you: ask them, and its card shows here once it is set up.
                    </Alert>
                )}
                {offered && picked && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>Credentials at {picked.name}</Typography>
                        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 1.5, maxWidth: '68ch' }}>
                            {environment === 'sandbox'
                                ? 'Use test credentials on a sandbox: a production key works, but what the sandbox does with it is real.'
                                : 'Check them with the booking system before adding: adding does not call it.'}
                        </Typography>
                        {withAccount && (
                            <TextField
                                size="small"
                                label="Account"
                                required
                                value={account}
                                onChange={(ev) => setAccount(ev.target.value.toLowerCase())}
                                error={!!accountError}
                                helperText={accountError || `${picked.name} keeps its credentials on a named account: its slug goes into the account's webhook addresses and can never change, e.g. lisbon.`}
                                inputProps={{ 'data-testid': 'add-account-slug', spellCheck: false, autoComplete: 'off' }}
                                sx={{ width: { xs: '100%', sm: 360 }, mb: 1.5 }}
                            />
                        )}
                        <CredentialFields fields={fields} values={values} onChange={setValues} errors={fieldErrors(adding.error)} disabled={adding.busy} autoFocus />
                        {probe && <ProbeResult probe={probe} onClose={() => setProbe(null)} />}
                        {!canCheck && (
                            <Alert severity="info" sx={{ my: 1 }} data-testid="no-probe">
                                {picked.name} cannot be checked from here. Add it: its card shows whether bookings arrive.
                            </Alert>
                        )}
                        {hasSettingsStep && settingsSchema && (
                            <Box sx={{ mt: 1.5 }} data-testid="add-settings">
                                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Settings (optional)</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {chosenCount ? `${chosenCount} ${chosenCount === 1 ? 'setting' : 'settings'} chosen` : 'Defaults apply'}
                                    </Typography>
                                    {!editingSettings && (
                                        <Button size="small" onClick={() => setEditingSettings(true)} disabled={adding.busy}>
                                            {chosenCount ? 'Change settings' : 'Choose settings'}
                                        </Button>
                                    )}
                                </Stack>
                                {editingSettings && (
                                    <SettingsEditor
                                        schema={settingsSchema}
                                        values={chosenSettings}
                                        level="connection"
                                        editable
                                        saveLabel="Use these settings"
                                        onSave={async (patch) => {
                                            setSettings((prev) => {
                                                const next = { ...prev };
                                                Object.entries(patch).forEach(([key, value]) => {
                                                    if (value === null) {
                                                        delete next[key];
                                                    } else {
                                                        next[key] = value;
                                                    }
                                                });
                                                return next;
                                            });
                                        }}
                                        onClose={() => setEditingSettings(false)}
                                    />
                                )}
                            </Box>
                        )}
                        {checking.error ? <ErrorAlert error={checking.error} /> : null}
                        {adding.error ? <ErrorAlert error={adding.error} /> : null}
                        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0, mt: 1.5, maxWidth: '68ch' }}>
                            {help ? `${help} ` : ''}
                            {picked.kind === 'fleet'
                                ? 'Vehicle positions are polled once it is added.'
                                : 'Its webhook addresses and token show on its card once it is added; its settings can be changed there too.'}
                        </Typography>
                    </Box>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    {offered && canCheck && (
                        <Button size="small" variant="outlined" onClick={check} disabled={!complete || checking.busy || adding.busy} startIcon={checking.busy ? <CircularProgress size={14} /> : undefined}>
                            Check
                        </Button>
                    )}
                    {offered && picked && (
                        <Button size="small" variant="contained" onClick={add} disabled={!complete || adding.busy} startIcon={adding.busy ? <CircularProgress size={14} /> : undefined}>
                            Add {picked.name}
                        </Button>
                    )}
                    <Button size="small" onClick={onClose} disabled={adding.busy}>Cancel</Button>
                </Stack>
            </Box>
        </Paper>
    );
};

export default AddConnectionFlow;
